'use client'

import { __cCollection2Service } from '@/services/client/__c.collection.service'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

interface UseCollectionBodyHtmlProps {
  slug: string | undefined
  initialBodyHtml?: string
}

/**
 * Hook để fetch collection body HTML theo path (slug)
 * Tự động refetch khi slug thay đổi
 * @param slug - Collection slug (có thể chứa concat values)
 * @param initialBodyHtml - Initial data từ server-side fetch (để hydrate)
 * @returns { data, isLoading, error }
 */
export function useCollectionBodyHtml({
  slug,
  initialBodyHtml,
}: UseCollectionBodyHtmlProps) {
  // Track slug ban đầu từ server để chỉ dùng initialData cho slug đó
  // (useState thay vì useRef: giá trị chỉ lấy ở lần render đầu tiên và không
  // bao giờ bị ghi đè, giống hệt hành vi cũ, nhưng đọc state trong render là an toàn)
  const [initialSlug] = useState(slug)

  const query = useQuery({
    queryKey: ['collection-body-html', slug],
    queryFn: async () => {
      if (!slug) {
        throw new Error('Slug is required')
      }

      return await __cCollection2Service.getBodyHtmlByCollectionPath(slug)
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5, // 5 phút
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Không refetch lần đầu - dùng initialData từ server
    // Chỉ set initialData cho slug ban đầu từ server
    // Khi slug thay đổi (filter), queryKey thay đổi → React Query tự động fetch query mới
    ...(initialBodyHtml && slug === initialSlug
      ? { initialData: initialBodyHtml }
      : {}),
    retry: 1,
  })

  // Priority: query.data > initialBodyHtml
  const data = query.data ?? initialBodyHtml

  return {
    data,
    isLoading: query.isLoading,
    error: query.error,
  }
}
